import React, { useContext, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreditCard,
  DollarSign,
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  Receipt,
  Download,
  FileDown,
  ArrowDown,
  Eye,
  BookOpen,
  Smartphone,
  Award,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import PaymentOptionsModal from "../../components/payments/PaymentOptionsModal";
import { AuthContext } from "../../contexts/AuthContext";
import {
  getMyStudentProfile,
  getStudentPaymentHistory,
} from "../../services/students";
import {
  getFees,
  generateStudentPaymentReceipt,
  downloadStudentPaymentReceipt,
} from "../../services/fees";
import { formatKSh } from "../../utils/currency";
import { toast } from "react-hot-toast";
import Pagination from "../../components/common/Pagination";

const StudentFees: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const queryClient = useQueryClient();
  const [selectedTerm, setSelectedTerm] = useState("current");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false,
  });

  const {
    data: studentData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["student-fees"],
    queryFn: () => getMyStudentProfile(),
    enabled: !!user?.id,
  });

  // Get student data early for dependent queries
  const student = studentData?.data;

  // Fetch payment history using the proper API endpoint
  const { data: paymentHistoryData } = useQuery({
    queryKey: ["studentPaymentHistory", student?._id],
    queryFn: () => getStudentPaymentHistory(student!._id),
    enabled: !!student?._id,
    staleTime: 1 * 60 * 1000,
  });

  // Update pagination when paymentHistory changes
  React.useEffect(() => {
    const paymentHistory = (() => {
      if (
        paymentHistoryData?.data?.paymentHistory &&
        paymentHistoryData.data.paymentHistory.length > 0
      ) {
        return paymentHistoryData.data.paymentHistory
          .filter(
            (payment: any) =>
              payment.status === "completed" || payment.status === "verified"
          )
          .map((payment: any) => ({
            id: payment._id || payment.referenceNumber,
            date: payment.paymentDate,
            amount: payment.amount,
            description: payment.notes || "Course Fee Payment",
            status: "completed",
            method: payment.paymentMethod || "cash",
            reference: payment.referenceNumber || payment._id,
            mpesaReceiptNumber: payment.mpesaDetails?.transactionId,
            transactionId: payment.mpesaDetails?.transactionId,
            invoicesPaid: payment.invoicesPaid || [],
          }))
          .sort(
            (a: any, b: any) =>
              new Date(b.date).getTime() - new Date(a.date).getTime()
          );
      }
      return [];
    })();

    const total = paymentHistory.length;
    const pages = Math.ceil(total / pagination.limit);
    setPagination((prev) => ({
      ...prev,
      total,
      pages,
      hasNext: prev.page < pages,
      hasPrev: prev.page > 1,
    }));
  }, [
    paymentHistoryData?.data?.paymentHistory,
    pagination.limit,
    pagination.page,
  ]);

  // Prevent e-course students from accessing regular student pages
  if (student && student.studentType === "ecourse") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-orange-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600 mb-4">
            Fee management is not available for e-course students.
          </p>
          <a
            href="/student/ecourse-dashboard"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to E-Learning Dashboard
          </a>
        </div>
      </div>
    );
  }

  // Fetch fee structures (keeping this for compatibility)
  const { data: feesData } = useQuery({
    queryKey: ["student-fee-structures", student?._id],
    queryFn: () =>
      getFees({
        studentId: student?._id,
        limit: 10,
      }),
    enabled: !!student?._id,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl border border-red-200/60 rounded-2xl p-6 shadow-lg">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-red-900 font-semibold text-lg">
                Error Loading Fee Information
              </h3>
              <p className="text-red-700 mt-2">
                Unable to load your fee details. Please try refreshing the page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handlePaymentSuccess = () => {
    // Refresh the student data to reflect the new payment
    queryClient.invalidateQueries({ queryKey: ["student-fees"] });
    queryClient.invalidateQueries({
      queryKey: ["student-fee-structures", student?._id],
    });
    queryClient.invalidateQueries({
      queryKey: ["studentPaymentHistory", student?._id],
    });

    toast.success("Payment initiated successfully!");
    setShowPaymentModal(false);
  };

  // Use fee data from payment history API (more accurate with totalAmountDue field)
  const fees = paymentHistoryData?.data?.feeSummary || student?.fees;

  // Get real fee data
  const realFeeStructures = feesData?.data || [];

  // Payment history data - use API response for accurate data
  const paymentHistory = (() => {
    if (
      paymentHistoryData?.data?.paymentHistory &&
      paymentHistoryData.data.paymentHistory.length > 0
    ) {
      return paymentHistoryData.data.paymentHistory
        .filter(
          (payment: any) =>
            payment.status === "completed" || payment.status === "verified"
        )
        .map((payment: any) => ({
          id: payment._id || payment.referenceNumber,
          date: payment.paymentDate,
          amount: payment.amount,
          description: payment.notes || "Course Fee Payment",
          status: "completed",
          method: payment.paymentMethod || "cash",
          reference: payment.referenceNumber || payment._id,
          mpesaReceiptNumber: payment.mpesaDetails?.transactionId,
          transactionId: payment.mpesaDetails?.transactionId,
          invoicesPaid: payment.invoicesPaid || [],
        }))
        .sort(
          (a: any, b: any) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
        );
    }

    return [];
  })();

  // Calculate paginated payments
  const paginatedPayments = paymentHistory.slice(
    (pagination.page - 1) * pagination.limit,
    pagination.page * pagination.limit
  );

  // Handler for viewing receipt
  const handleViewReceipt = async (payment: any) => {
    try {
      const response = await generateStudentPaymentReceipt(
        student?._id || "",
        payment.reference
      );

      if (response.success && response.data.receiptUrl) {
        // Convert relative URL to absolute URL
        const API_URL =
          import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
        const absoluteUrl = response.data.receiptUrl.startsWith("http")
          ? response.data.receiptUrl
          : `${API_URL}${response.data.receiptUrl}`;

        // Open receipt in a new window/tab
        window.open(absoluteUrl, "_blank");
      } else {
        throw new Error(response.message || "Failed to generate receipt");
      }
    } catch (error) {
      console.error("Error viewing receipt:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to view receipt: ${errorMessage}`);
    }
  };

  // Handler for downloading receipt
  const handleDownloadReceipt = async (payment: any) => {
    try {
      const blob = await downloadStudentPaymentReceipt(
        student?._id || "",
        payment.reference
      );

      // Ensure we have a valid blob
      if (!blob || !(blob instanceof Blob) || blob.size === 0) {
        throw new Error(
          `Invalid response format. Received: ${typeof blob}, size: ${
            blob?.size
          }`
        );
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `receipt-${payment.reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Receipt downloaded successfully");
    } catch (error) {
      console.error("Error downloading receipt:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to download receipt: ${errorMessage}`);
    }
  };

  // Upcoming payments from API invoices
  const upcomingPayments = (() => {
    // Use invoices from payment history API
    if (
      paymentHistoryData?.data?.invoices &&
      paymentHistoryData.data.invoices.length > 0
    ) {
      return paymentHistoryData.data.invoices
        .filter((invoice: any) => {
          const balance =
            (invoice.totalAmount || 0) - (invoice.amountPaid || 0);
          return balance > 0; // Only show unpaid/partially paid invoices
        })
        .map((invoice: any) => {
          const dueDate = new Date(invoice.dueDate);
          const isPastDue = dueDate < new Date();
          const expected =
            (invoice.totalAmount || 0) -
            (invoice.discountAmount || 0) -
            (invoice.scholarshipAmount || 0);
          const balance = expected - (invoice.amountPaid || 0);
          const invoiceDate = new Date(invoice.dueDate || invoice.createdAt);
          const monthLabel = invoiceDate.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          });

          return {
            id: invoice._id || invoice.invoiceNumber,
            dueDate: invoice.dueDate,
            amount: balance,
            description: `${monthLabel} - ${invoice.type || "monthly"} Fee`,
            status: isPastDue ? "overdue" : "pending",
          };
        })
        .sort(
          (a, b) =>
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
        );
    }

    // Fallback: Check course-based installments from student data
    if (student?.coursesInfo && student.coursesInfo.length > 0) {
      const installments: any[] = [];

      student.coursesInfo.forEach((course: any) => {
        if (
          course.feeStructure?.installmentPlan?.enabled &&
          course.feeStructure.installmentPlan.schedule
        ) {
          course.feeStructure.installmentPlan.schedule.forEach(
            (installment: any) => {
              const dueDate = new Date(installment.dueDate);
              const isPastDue = dueDate < new Date();

              installments.push({
                id: installment._id || installment.id,
                dueDate: installment.dueDate,
                amount: installment.amount,
                description: `${course.name} (${course.level}) - Installment ${installment.installmentNumber}`,
                status: isPastDue ? "overdue" : "pending",
                courseId: course._id,
                courseName: course.name,
              });
            }
          );
        }
      });

      installments.sort(
        (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
      );

      if (installments.length > 0) {
        return installments;
      }
    }

    return [];
  })();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-600 bg-green-100";
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      case "overdue":
        return "text-red-600 bg-red-100";
      case "upcoming":
        return "text-blue-600 bg-blue-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4" />;
      case "pending":
        return <Clock className="h-4 w-4" />;
      case "overdue":
        return <AlertCircle className="h-4 w-4" />;
      case "upcoming":
        return <Calendar className="h-4 w-4" />;
      default:
        return <CreditCard className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-2 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Fee Management
          </h1>
          <p className="text-slate-600 mt-1 sm:mt-2 text-xs sm:text-sm lg:text-base">
            View your fee structure, payment history, and make payments
          </p>
        </div>

        {/* Fee Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 xl:gap-6 mb-4 sm:mb-6 lg:mb-8">
          {/* Total Fee Structure */}
          <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-slate-200/60 p-2.5 sm:p-4 lg:p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-slate-600 text-[10px] sm:text-xs lg:text-sm font-medium mb-0.5 sm:mb-1 truncate">
                  Fee Structure
                </p>
                <p className="text-sm sm:text-lg lg:text-xl xl:text-2xl font-bold text-slate-900 truncate">
                  {formatKSh(fees?.totalFeeStructure || 0)}
                </p>
              </div>
              <div className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-lg sm:rounded-xl lg:rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0 shadow-lg ml-1 sm:ml-2">
                <DollarSign className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-white" />
              </div>
            </div>
          </div>

          {/* Total Paid */}
          <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-slate-200/60 p-2.5 sm:p-4 lg:p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-slate-600 text-[10px] sm:text-xs lg:text-sm font-medium mb-0.5 sm:mb-1 truncate">
                  Total Paid
                </p>
                <p className="text-sm sm:text-lg lg:text-xl xl:text-2xl font-bold text-slate-900 truncate">
                  {formatKSh(fees?.totalPaid || 0)}
                </p>
              </div>
              <div className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-lg sm:rounded-xl lg:rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0 shadow-lg ml-1 sm:ml-2">
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-white" />
              </div>
            </div>
          </div>

          {/* Outstanding Balance */}
          <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-slate-200/60 p-2.5 sm:p-4 lg:p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-slate-600 text-[10px] sm:text-xs lg:text-sm font-medium mb-0.5 sm:mb-1 truncate">
                  Outstanding Balance
                </p>
                <p className="text-sm sm:text-lg lg:text-xl xl:text-2xl font-bold text-slate-900 truncate">
                  {formatKSh(fees?.totalBalance || 0)}
                </p>
              </div>
              <div className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-lg sm:rounded-xl lg:rounded-2xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-lg ml-1 sm:ml-2">
                <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-white" />
              </div>
            </div>
          </div>

          {/* Fee Status */}
          <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-slate-200/60 p-2.5 sm:p-4 lg:p-6 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-slate-600 text-[10px] sm:text-xs lg:text-sm font-medium mb-0.5 sm:mb-1 truncate">
                  Fee Status
                </p>
                <p className="text-sm sm:text-lg lg:text-xl xl:text-2xl font-bold text-slate-900 capitalize truncate">
                  {fees?.feeStatus || "N/A"}
                </p>
              </div>
              <div className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-lg sm:rounded-xl lg:rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-lg ml-1 sm:ml-2">
                <CreditCard className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Course Fees Breakdown */}
          <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-slate-200/60 p-4 sm:p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-slate-900">
                Course Fees
              </h2>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
                <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {student?.coursesInfo && student.coursesInfo.length > 0 ? (
                student.coursesInfo.map((course: any) => (
                  <div
                    key={course._id}
                    className="bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-lg sm:rounded-xl p-3 sm:p-4 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-2 sm:mb-3 gap-2">
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base flex-1 min-w-0">
                        {course.name}
                      </h3>
                      <span className="text-[10px] sm:text-xs text-slate-600 font-medium bg-white px-2 sm:px-3 py-1 rounded-lg shadow-sm whitespace-nowrap flex-shrink-0">
                        {course.code}
                      </span>
                    </div>

                    <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Monthly Fee:</span>
                        <span className="font-semibold text-slate-900">
                          {formatKSh(
                            course.feeStructure?.perPeriodAmount ||
                              course.fees?.tuitionFee ||
                              0
                          )}
                        </span>
                      </div>

                      {course.feeStructure?.installmentPlan?.enabled && (
                        <div className="flex items-center space-x-1 text-[10px] sm:text-xs bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg shadow-md w-fit">
                          <Calendar className="h-3 w-3" />
                          <span>
                            {
                              course.feeStructure.installmentPlan
                                .numberOfInstallments
                            }{" "}
                            installments available
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 sm:py-8">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mx-auto mb-2 sm:mb-3">
                    <BookOpen className="h-6 w-6 sm:h-8 sm:w-8 text-slate-500" />
                  </div>
                  <p className="text-slate-500 text-xs sm:text-sm">
                    No courses enrolled
                  </p>
                </div>
              )}

              {/* Total Summary */}
              <div className="border-t border-slate-200 pt-3 sm:pt-4 mt-3 sm:mt-4">
                <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg sm:rounded-xl shadow-md">
                  <span className="text-xs sm:text-sm font-medium text-white">
                    Total Course Fees
                  </span>
                  <span className="font-bold text-base sm:text-lg text-white">
                    {formatKSh(fees?.totalFeeStructure || 0)}
                  </span>
                </div>
              </div>

              {/* Pay Now Button */}
              {fees?.totalBalance && fees.totalBalance > 0 && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  disabled
                  className="w-full mt-3 sm:mt-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-2.5 sm:py-3 px-4 rounded-lg sm:rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 font-semibold text-sm sm:text-base"
                >
                  <Smartphone className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Make Payment</span>
                </button>
              )}
            </div>
          </div>

          {/* Payment History & Upcoming Payments */}
          <div className="lg:col-span-2">
            <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-slate-200/60 p-4 sm:p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
                <h2 className="text-base sm:text-lg lg:text-xl font-bold text-slate-900">
                  Payment Records
                </h2>
                <select
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                  className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-slate-300 rounded-lg sm:rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <option value="current">Current Term</option>
                  <option value="previous">Previous Term</option>
                  <option value="all">All Terms</option>
                </select>
              </div>

              {/* Upcoming Payments */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-slate-900 mb-3 sm:mb-4 flex items-center space-x-2">
                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  <span>Upcoming Payments</span>
                </h3>
                {upcomingPayments.length > 0 ? (
                  <div className="space-y-2 sm:space-y-3">
                    {upcomingPayments.map((payment) => (
                      <div
                        key={payment.id}
                        className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-lg sm:rounded-xl hover:shadow-lg transition-all duration-300 gap-2 sm:gap-4"
                      >
                        <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
                          <div
                            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md flex-shrink-0 ${
                              payment.status === "overdue"
                                ? "bg-gradient-to-br from-red-500 to-pink-500"
                                : payment.status === "pending"
                                ? "bg-gradient-to-br from-yellow-500 to-orange-500"
                                : "bg-gradient-to-br from-blue-500 to-indigo-500"
                            }`}
                          >
                            <div className="text-white">
                              {getStatusIcon(payment.status)}
                            </div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-semibold text-slate-900 text-xs sm:text-sm lg:text-base truncate">
                              {payment.description}
                            </h4>
                            <p className="text-[10px] sm:text-xs text-slate-600 flex items-center space-x-1 mt-0.5 sm:mt-1">
                              <Calendar className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">
                                Due:{" "}
                                {new Date(payment.dueDate).toLocaleDateString()}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-bold text-sm sm:text-base lg:text-lg text-slate-900 whitespace-nowrap">
                            {formatKSh(payment.amount)}
                          </p>
                          <span
                            className={`inline-flex px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-semibold rounded-md sm:rounded-lg shadow-md whitespace-nowrap ${
                              payment.status === "overdue"
                                ? "bg-gradient-to-r from-red-500 to-pink-500 text-white"
                                : payment.status === "pending"
                                ? "bg-gradient-to-r from-yellow-500 to-orange-500 text-white"
                                : "bg-gradient-to-r from-blue-500 to-indigo-500 text-white"
                            }`}
                          >
                            {payment.status.charAt(0).toUpperCase() +
                              payment.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mx-auto mb-2 sm:mb-3">
                      <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-slate-500" />
                    </div>
                    <p className="text-slate-500 text-xs sm:text-sm">
                      No upcoming payments
                    </p>
                  </div>
                )}
              </div>

              {/* Payment History */}
              <div>
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-slate-900 mb-3 sm:mb-4 flex items-center space-x-2">
                  <Receipt className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  <span>Payment History</span>
                </h3>
                {paginatedPayments.length > 0 ? (
                  <div className="space-y-2 sm:space-y-3">
                    {paginatedPayments.map((payment: any) => (
                      <div
                        key={payment.id}
                        className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-br from-slate-50 to-green-50 border border-slate-200 rounded-lg sm:rounded-xl hover:shadow-lg transition-all duration-300 gap-2 sm:gap-4"
                      >
                        <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-md flex-shrink-0">
                            <div className="text-white">
                              {getStatusIcon(payment.status)}
                            </div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-semibold text-slate-900 text-xs sm:text-sm lg:text-base truncate">
                              {payment.description}
                            </h4>
                            <p className="text-[10px] sm:text-xs text-slate-600 flex items-center flex-wrap gap-1 mt-0.5 sm:mt-1">
                              <Calendar className="h-3 w-3 flex-shrink-0" />
                              <span className="whitespace-nowrap">
                                {new Date(payment.date).toLocaleDateString()}
                              </span>
                              <span className="text-slate-400">•</span>
                              <span className="truncate">
                                {payment.method === "mpesa"
                                  ? "M-Pesa"
                                  : payment.method}
                              </span>
                            </p>
                            <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1 truncate">
                              Ref: {payment.reference}
                            </p>
                            {/* Show M-Pesa specific details if available */}
                            {payment.method === "mpesa" &&
                              (payment.mpesaReceiptNumber ||
                                payment.transactionId) && (
                                <div className="text-[10px] sm:text-xs bg-gradient-to-r from-green-500 to-emerald-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md sm:rounded-lg mt-1 sm:mt-2 inline-block shadow-md">
                                  {payment.mpesaReceiptNumber && (
                                    <div className="flex items-center space-x-1">
                                      <Smartphone className="h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0" />
                                      <span className="truncate">
                                        M-Pesa Receipt:{" "}
                                        {payment.mpesaReceiptNumber}
                                      </span>
                                    </div>
                                  )}
                                  {payment.transactionId && (
                                    <div className="mt-0.5 truncate">
                                      Transaction ID: {payment.transactionId}
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-bold text-sm sm:text-base lg:text-lg text-slate-900 whitespace-nowrap">
                            {formatKSh(payment.amount)}
                          </p>
                          <div className="flex items-center justify-end mt-1.5 sm:mt-2">
                            <Download
                              className="w-7 h-7 sm:w-9 sm:h-9 text-blue-500 drop-shadow-lg "
                              onClick={() => handleDownloadReceipt(payment)}
                              type="button"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Receipt className="h-8 w-8 sm:h-10 sm:w-10 text-slate-500" />
                    </div>
                    <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-slate-900">
                      No successful payments found
                    </h3>
                    <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-slate-600">
                      Your successful payment records will appear here once you
                      complete payments.
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {paymentHistory.length > 0 && (
                <div className="mt-6 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6">
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.pages}
                    onPageChange={(page) =>
                      setPagination({ ...pagination, page })
                    }
                    pageSize={pagination.limit}
                    onPageSizeChange={(size) => {
                      setPagination({ ...pagination, limit: size, page: 1 });
                    }}
                    showPageSizeSelector={true}
                    className="w-full"
                  />
                </div>
              )}

              {/* Summary */}
              {paymentHistory.length > 0 && (
                <div className="mt-6 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-4">
                  <p className="text-sm text-slate-700 font-semibold">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}{" "}
                    of {pagination.total} payments
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Options Modal */}
        {showPaymentModal && (
          <PaymentOptionsModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            maxAmount={fees?.totalBalance || 0}
            studentInfo={{
              name: `${student?.userInfo?.firstName || ""} ${
                student?.userInfo?.lastName || ""
              }`.trim(),
              studentId: student?.studentId || "",
            }}
            onPaymentSuccess={handlePaymentSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default StudentFees;
